import { Page as PDFPage, View } from "@react-pdf/renderer";
import type { PropsWithChildren } from "react";
import React from "react";
import { DEFAULT_PAGE_INSETS, type PageInsets } from "./_defaults";
import { Heading } from "./heading";
import {
	AddressInformation,
	CompanyInformation,
	FoldMarks,
	Logo,
	Meta,
	PageNumber,
	Signature,
} from "./misc";
import { Decoration } from "./misc/decoration";
import { Footer } from "./misc/footer";
import { Spacer } from "./spacer";
import { tw } from "./tailwind";
import { Text } from "./text";
import type { MetaItem, Orientation, PageSize } from "./types";

/** Props Interface */
interface PageProps extends PropsWithChildren {
	insets?: Partial<PageInsets>;
	orientation?: Orientation;
	pageSize?: PageSize;
	metaItems?: MetaItem[];
	title?: string;
	subTitle?: string;
	addressInformation?: Array<string>;
	documentId?: string;
}

export function Page({
	children,
	insets,
	orientation = "portrait",
	pageSize = "A4",
	metaItems = [],
	title,
	subTitle,
	addressInformation = [],
	documentId,
}: PageProps) {
	return (
		<React.Fragment>
			<PDFPage
				size={pageSize}
				orientation={orientation}
				dpi={300}
				style={{
					paddingLeft: `${insets?.left ?? DEFAULT_PAGE_INSETS.left}px`,
					paddingRight: `${insets?.right ?? DEFAULT_PAGE_INSETS.right}px`,
					paddingTop: `${insets?.top ?? DEFAULT_PAGE_INSETS.top}px`,
					paddingBottom: `${insets?.bottom ?? DEFAULT_PAGE_INSETS.bottom}px`,
				}}
			>
				<Decoration />
				<FoldMarks />
				<PageNumber documentId={documentId} />
				<Footer />
				<View style={{ flex: 1 }}>
					<Logo />
					<View
						fixed
						style={{ flexDirection: "row" }}
					>
						<AddressInformation addressInformation={addressInformation} />
						<CompanyInformation />
					</View>
					<Meta items={metaItems} />
					{title && (
						<React.Fragment>
							<View
								wrap={false}
								fixed
							>
								<Heading>{title}</Heading>
								{subTitle && <Text style={tw("text-sm")}>{subTitle}</Text>}
							</View>
							<Spacer fixed />
						</React.Fragment>
					)}
					{children}
					<Signature />
				</View>
			</PDFPage>
		</React.Fragment>
	);
}
